import level1Activities from "./level1Activities";
import level1Catalog from "./level1Catalog";
import { costCategories, costItemNotes, costItems } from "./level1Costs";

export default {
  // Intro screen
  introTitle: "Tell us more about your project",
  introSubtitle: "Complete the form to set up your carbon project",
  startApplication: "Start application",
  introSummary: "Summary",
  introSummaryDesc: "Project general informations",
  introData: "Data",
  introDataDesc: "Boundary files, PDD, and field data",
  introCredits: "Credits",
  introCreditsDesc: "Carbon credit projections",
  introCosts: "Costs",
  introCostsDesc: "Costs across project timeline",

  // Navigation
  learnMore: "Learn more",
  previous: "Previous",
  continue: "Continue",
  submit: "Submit",
  backToDrafts: "Back to drafts",
  submissionFailed:
    "Project submission could not be completed. Your draft remains saved. Try again or contact support if the issue continues.",
  auditorNotApproved:
    "The selected auditor is not approved in the active on-chain trust registry. An administrator must approve the auditor from the Admin page before you submit this project.",
  submissionSupportReference: "Support reference: {reference}",
  contactSupport: "Contact support",
  stagePricingFailed:
    "Stage pricing could not be calculated. Please try again.",
  editCosts: "Edit costs",

  // Step 1: Summary
  projectName: "Project Name",
  projectNamePlaceholder: "Project name",
  projectDescription: "Project Description",
  projectDescriptionPlaceholder:
    "A comprehensive overview of acne, goals etc.",
  projectType: "Project type",

  // Step 2: Data
  projectBoundary: "Project Boundary",
  projectBoundaryDesc: "Geographic boundary of your project area",
  uploadKmlFile: "Upload kml file",
  uploadKmlKmz: "Upload KML/KMZ",
  uploading: "Uploading...",
  uploadError: "Upload failed. Please try again.",
  calculatingCarbonEstimate: "Calculating carbon estimate…",
  computingCredits: "Computing carbon credits...",
  fieldData: "Field data",
  fieldDataDesc: "Plot measurements and ground-truth data",
  existingDataset: "Existing dataset",
  uploadDataset: "upload dataset",
  noDataset: "No dataset",
  useRemoteSensing: "Use Remote sensing",
  certification: "Certification",
  methodology: "Methodology",
  pddInfo:
    "A Project Design Document (PDD) will need to be created or uploaded based on your methodology selection.",
  landOwnershipStatus: "Land Ownership Status",
  landOwnershipPlaceholder: "Select tenure type",
  landOwnershipHelper:
    "Proposed addition derived from Brazilian land-tenure conventions (CCIR/matrícula, posse, assentamento, terra indígena, quilombola, UC, concessão).",
  landStatusOptions: {
    private_title: "Private",
    possession: "Possession (informal)",
    agrarian_settlement: "Agrarian reform settlement",
    indigenous_territory: "Indigenous territory",
    quilombola: "Quilombola community",
    conservation_unit: "Conservation unit (RPPN, APA, …)",
    forest_concession: "Forest concession",
    other: "Other (specify)",
  },
  // Step 3: Credits
  estimationMethod: "Estimation method",
  fieldPlotData: "Field Plot Data",
  biomassCalculator: "Biomass calculator",
  plotBasedEstimation: "Plot-Based Estimation",
  remoteSensingEstimation: "Remote-Sensing Estimation",
  plotsAnalyzed: "{count} plots analyzed across {area} hectares",
  projected: "Projected",
  lowEstimate: "Low Estimate",
  midEstimate: "Mid Estimate",
  highEstimate: "High Estimate",
  years: "{n} years",
  creditsNotReady: "Return to the Data step to upload your boundary file and dataset to see credit estimates here.",
  creditsComputing: "Calculating credit estimates. Please wait before continuing.",
  creditsComputeFailed:
    "Credit estimates could not be calculated. The server did not return a usable response. Please try again or contact support if this continues.",
  creditsComputeFailedWithDetail: "Credit estimates could not be calculated: {error}",
  yearsProjected: "{count} years projected",

  // Step 4: Costs
  costSetting: "Cost setting",
  costSettingDesc: "Base configuration for cost calculations",
  currency: "Currency",
  startYear: "Start Year",
  durationYears: "Duration (years)",
  durationYearsValue: "{years} years",
  timelineMirror: "(set on Timeline tab)",
  perYearBreakdownTitle: "Per-year cost breakdown",
  perYearBreakdownDesc:
    "Suggestion only — derived from your Timeline schedule. The editable totals above remain the source of truth for the materializer.",
  year: "Year",
  totalLabel: "Total",
  cost: {
    validation: "Validation",
    monitoring: "Monitoring",
    verification: "Verification",
    issuance: "Issuance",
    community: "Community",
  },
  otherCertifierFeesTitle: "Other certifier fees",
  otherCertifierFeesDesc:
    "Transactional and methodology fees the certifier may charge orthogonal to your project lifecycle. Surfaced for visibility; not added to the project cost totals.",
  requiresQuoteTitle: "Requires quote",
  landownerCompensation: {
    title: "Landowner compensation",
    desc: "Optional investor-funded compensation for private-land projects before credits are saleable.",
    include: "Include compensation cost",
    total: "Total",
    monthly: "Monthly",
    monthlyPerFamily: "Monthly per family",
    estimatedFamilies: "Estimated beneficiary families",
    estimatedFamiliesInvalid: "Enter a whole number greater than zero.",
    months: "Months",
    stageCostIncluded: "Distributed across applicable project stage costs.",
    allocation: "Allocation",
    payoutStart: "Payout start",
    startStage: "Start after stage",
    startDate: "Start date",
    maxMonths: "Maximum months",
    removedReason: "Removal reason",
    removedReasonPlaceholder: "Reason for removing this cost",
    payoutProjectStart: "Project start",
    payoutAfterStage: "After selected stage is funded",
    payoutExplicitDate: "Explicit date",
    allocationUniform: "Evenly across stages",
    allocationUpfront: "Upfront escrow",
    basis: "Basis",
    winningComponent: "Winning component",
    confidence: "Confidence",
    estimateOnly: "Estimate only",
    productionBlocked: "Not production usable yet",
  },
  communityBenefitPayout: {
    title: "Community benefit payout",
    desc: "Optional investor-funded benefit payout for eligible REDD+ and conservation projects.",
    include: "Include community benefit payout",
    total: "Total",
    monthly: "Monthly",
    monthlyPerFamily: "Monthly per family",
    monthlyPayoutPerFamily: "Monthly payout per family (BRL)",
    monthlyPayoutPerFamilyInvalid: "Enter a monthly amount greater than zero.",
    amountInvalid: "Enter an amount greater than zero.",
    estimatedFamilies: "Estimated beneficiary families",
    estimatedFamiliesInvalid: "Enter a positive whole number of families.",
    months: "Months",
    allocation: "Allocation",
    payoutStart: "Payout start",
    startStage: "Start after stage",
    startDate: "Start date",
    maxMonths: "Maximum months",
    removedReason: "Removal reason",
    removedReasonPlaceholder: "Reason for removing this payout",
    payoutProjectStart: "Project start",
    payoutAfterStage: "After selected stage is funded",
    payoutExplicitDate: "Explicit date",
    allocationUniform: "Evenly across stages",
    allocationUpfront: "Upfront escrow",
    stageCostIncluded: "Distributed across applicable project stage costs.",
    basis: "Basis",
    winningComponent: "Winning component",
    confidence: "Confidence",
    estimateOnly: "Estimate only",
    productionBlocked: "Not production usable yet",
    notCalculable: "Not calculable from the current source, credit, and beneficiary inputs.",
    notCalculableAction: "Complete the benefit-sharing agreement and beneficiary-family estimate to calculate this payout.",
    missingInputs: "Required inputs",
    predictionLadder: "Calculation paths",
    estimateTag: "Estimate",
    estimateRung: "Calculation rung",
    revenueBasis: "Revenue basis",
    sources: "Sources",
    beneficiaries: "Beneficiaries",
    perHousehold: "Per household",
    perHouseholdMonthly: "Per household per month",
    ladder: {
      sourced_minimum: "Signed agreement or sourced legal/standard minimum.",
      pct_of_forecast_revenue: "Lifetime forecast credits, selected price, and a sourced benefit share.",
      opportunity_cost_floor: "Eligible community area and a community-attributed opportunity-cost floor.",
      unavailable: "A signed agreement is required when no prediction path is usable.",
    },
    yes: "Yes",
    audit: {
      platform_policy_proxy: "Platform policy proxy",
      pct_of_forecast_revenue: "Percent of forecast carbon revenue",
      proxy_per_hectare: "Per-hectare proxy policy",
      proxy_per_household: "Per-household proxy policy",
      unavailable: "Unavailable",
      platform_policy_proxy_estimate_only:
        "This is a platform policy proxy estimate only.",
      area_overlap_unchecked:
        "Area-overlap review is still unchecked, so production use is blocked.",
      benefit_share_reference_unverified:
        "The matched benefit-share reference is not verified for this standard.",
      benefit_share_reference_requires_approval:
        "The benefit-share reference requires admin/legal approval.",
      community_benefit_proxy_policy_missing:
        "No active community-benefit proxy policy is available.",
      community_benefit_proxy_policy_has_no_computable_rate:
        "The active proxy policy has no computable rate for this draft.",
      community_benefit_proxy_policy_missing_provenance:
        "The proxy policy is missing approval/source provenance.",
      community_benefit_source_provenance_missing:
        "The sourced benefit amount is missing source provenance.",
      community_benefit_fixture_value_rejected:
        "A fixture-like benefit amount was rejected.",
      community_benefit_estimate_unavailable:
        "The community benefit estimate is not calculable from the current inputs.",
      forecast_revenue_missing:
        "Forecast credits or selected price are missing.",
      community_benefit_forecast_revenue_missing:
        "Forecast credits or selected price are missing.",
      community_benefit_forecast_credit_estimate_refused:
        "The current carbon-credit estimate was refused and cannot be used for benefit prediction.",
      community_benefit_forecast_credit_estimate_missing:
        "A current carbon-credit estimate is required.",
      forecast_credit_estimate_refused:
        "The current carbon-credit estimate was refused.",
      benefit_compensation_overlap_review:
        "Landowner compensation and community benefit overlap requires review before allocation.",
      signed_benefit_sharing_agreement_required:
        "A signed benefit-sharing agreement is required for this driver/context.",
      outside_commercial_driver_requires_agreement:
        "Outside commercial-driver projects require a signed benefit-sharing agreement.",
      benefit_share_revenue_basis_requires_review:
        "The matched revenue basis requires legal/admin review before it can be used.",
      estimate_only_prediction:
        "This estimate is investment-stage only and cannot be used for production.",
      no_beneficiary_identified:
        "Beneficiaries have not been identified.",
      beneficiary_kyc_not_verified_for_direct_payout:
        "Beneficiary KYC is not verified for direct payout.",
      safeguards_failed_or_missing:
        "Safeguards evidence is missing or not approved.",
      proxy_fixed_minimum_floor_applied:
        "The platform fixed minimum raised the computed estimate.",
      low: "Low",
      medium: "Medium",
      high: "High",
      blocked: "Blocked",
    },
  },
  scaleClassification: "Scale Classification (FR.CER.001)",
  scaleClassificationDesc: "Auto-computed from Credits",
  estimatedTco2eYear: "Estimated tCO2e/year",
  projectScale: "Project Scale",
  smallScale: "Small (less than tCO₂e/year < 16,000)",
  largeScale: "Large (≥ 16,000 tCO₂e/year)",
  loadingCostEstimates: "Loading cost estimates...",
  validationVvb: "Validation / VVB Arrangement",
  vvbBasedOn: "Based on DC.GOV.002 & DC.GOV.003",
  vvbSmallScaleTitle: "Small Scale — Tero Hires Auditor",
  vvbSmallScaleCost: "Validation Cost: {cost} (locked)",
  teroSmallScaleFeeText:
    "converted to R$ in this estimate (source fee USD 1,000/project; acceptance criteria USD 1,000 + USD 200/rural property)",
  vvbSmallScaleDesc:
    "For Small Scale projects, Tero Carbon contracts the VVB directly to ensure conflict-of-interest compliance. The validation cost is fixed at Tero's Standard Fee.",
  oneTime: "One time",
  recurring: "Recurring",
  addItem: "add item",
  itemName: "Item name",
  amount: "Amount",
  units: {
    credit: "credit",
    year: "year",
  },
  costSummary: "Cost Summary",
  year1Total: "Year 1 Total",
  avgAnnual: "Avg. Annual",
  lifetimeTotal: "Lifetime Total",
  costPerCredit: "Cost / Credit",
  costBreakdown: "Cost Breakdown",

  // Step 5: Price Quote
  priceQuoteTitle: "Price Quote",
  priceQuoteDesc: "Select the sellable pricing strategy you want to apply. Diagnostic floors are shown separately.",
  recompute: "Recompute",
  loadingQuote: "Computing price quote…",
  priceQuoteFailed: "Price quote failed. Sign in again or try recalculating before continuing.",
  loadingStagePricing: "Computing stage pricing…",
  indicativePrice: "Indicative price",
  bandFloor: "Floor",
  bandSuggested: "Suggested",
  bandStretch: "Stretch",
  bandCeiling: "Ceiling",
  priceOptionRecommended: "Recommended contract-feasible price",
  priceOptionFeasible: "Contract-feasible price",
  priceOptionTiers: {
    floor: "Floor",
    suggested: "Suggested",
    stretch: "Stretch",
    ceiling: "Ceiling",
  },
  roiOnCashCosts: "Return on cash costs",
  breakEvenFloorLabel: "Break-even floor",
  breakEvenDiagnostic:
    "{price} economic break-even including opportunity cost. Suggested price is {margin} over this floor. Not sellable.",
  singleFeasiblePriceAlgorithm:
    "Only one pricing algorithm produced a contract-feasible price. Other algorithm results are shown below for comparison.",
  algorithmFeasibleDiagnostic:
    "{price} is this algorithm's contract-feasible result. Select one of the verified price-band options above to continue.",
  algorithmBelowFeasibleFloor:
    "This algorithm reaches {maximum}, below the {minimum} minimum required by stage funding and the investor allocation limit. It cannot be selected.",
  priceStrategies: {
    supply_curve_oc: "Supply Curve + Opportunity Cost",
    lcor: "Levelized Cost of Restoration",
    lcos: "Levelized Cost of Sequestration",
    macc_breakeven: "MACC Break-even",
    label_multiplier: "Label Multiplier",
    leaf_art_floor: "LEAF/ART Floor",
    frontier_pathway_comp: "Frontier Pathway Comparison",
    sovcm_median: "Ecosystem Marketplace Median",
    forward_shadow_price: "Forward Shadow Price",
  },
  priceStrategyNotes: {
    supply_curve_oc: "Supply-curve floor for {projectClass}",
    leaf_art_floor: "LEAF/ART published jurisdictional floor",
    sovcm_median: "SOVCM 2025 category median (demand-side)",
    forward_shadow_price: "REMIND-MAgPIE, 2010 USD, advanced economies",
    verra_nature_qha: "Verra Nature Framework Qha (not tCO2e - separate unit)",
    frontier_pathway_comp: "Frontier pathway average for {pathway}",
    lcor: "LCOR: {technique} in {biome}",
    peatland_lcor: "Peatland avoided-emissions LCOR [{variant}]",
    lcos: "LCOS {subclass} [{variant}]",
    macc_breakeven: "MACC {projectClass} [{subType}]",
    label_multiplier: "Label multiplier on tCO2e base (+{premium})",
  },
  priceStrategyDiagnostics: {
    supply_curve_oc:
      "{price} is the supply-curve result based on saved project costs and displaced land-use opportunity cost. It is not a feasible sale price for this draft.",
    lcor:
      "{price} is the levelized restoration-cost result. It is not a feasible sale price for this draft.",
    lcos:
      "{price} is the levelized sequestration-cost result. It is not a feasible sale price for this draft.",
    macc_breakeven:
      "{price} is the marginal abatement-cost result. It is not a feasible sale price for this draft.",
    default:
      "{price} is this algorithm's calculated result. It is not a feasible sale price for this draft.",
  },
  priceStrategyRoleLabels: {
    break_even_floor: "Break-even floor",
    binding_break_even_floor: "Binding floor",
    market_anchored_price: "Sellable price",
    market_reference_below_cost_floor: "Below cost floor",
    reference_price: "Reference only",
    sellable_price: "Sellable price",
    contract_infeasible_price: "Not selectable",
    algorithm_low_price: "Feasible algorithm price",
    algorithm_mid_price: "Feasible algorithm price",
    algorithm_high_price: "Feasible algorithm price",
  },
  priceProjectClasses: {
    aud_redd_plus: "avoided deforestation / REDD+",
    apd: "avoided planned deforestation",
    acogs: "avoided ecosystem conversion",
    ifm: "improved forest management",
    arr: "ARR restoration",
    blue_carbon: "blue carbon",
    peatland: "peatland",
    grasslands: "grasslands",
    smallholder_agroforestry: "smallholder agroforestry",
    native_veg_brazil: "native vegetation restoration in Brazil",
    alm_soc: "agricultural land management / soil carbon",
    urban_forestry: "urban forestry",
    biodiversity_wrapper: "biodiversity wrapper",
    engineered_removal: "engineered carbon removal",
    methane_waste: "methane waste",
    community_development: "community development",
    energy_industry: "energy and industry",
  },

  // Stage pricing block
  stagePricingTitle: "Stage Pricing",
  stagePricingDesc: "Per-stage price and ROI for each year of the crediting period. Click a year to expand.",
  stagePricingNoData: "No stage pricing data available yet. Complete the Price step and continue to see results here.",
  stagePricingNoStageDetail: "No stage detail available.",
  yearLabel: "Year {n}",
  showMoreYears: "+ Show {count} more year | + Show {count} more years",
  showFewerYears: "Show fewer years",
  avgRoi: "ROI",

  // Step 6: Review
  reviewTitle: "Review",
  missingPdd: "Missing PDD",
  missingPddDesc:
    "A PDD is required before you can submit your project.",
  createPdd: "Create PDD",
  upload: "Upload",
  uploadPdd: "Attach",
  replacePdd: "Replace PDD",
  pddUploaded: "PDD attached",
  pddUploadedDesc: "The PDD file is attached.",
  location: "Location",
  projectArea: "Project area",
  projectAreaValue: "{area} ha",
  totalCredits: "Total credits",
  peakYear: "Peak year ({year})",
  estimationMethodLabel: "Estimation Method",
  lifetimeTotalCosts: "Lifetime Total Costs",
  annualAverage: "Annual average",
  costPerCreditLabel: "Cost per Credit",

  // Revenue block
  lifetimeTotalRevenue: "Lifetime Total Revenue",
  year1Revenue: "Year 1 Revenue",
  avgRevenue: "Avg Revenue",

  // Profit block
  lifetimeProfit: "Lifetime Profit",
  year1Profit: "First issuance year profit",
  annualAverageRoi: "Annual Average ROI",
  profitPerCredit: "Profit per Credit",

  mapNotAvailable: "Map preview unavailable. Upload a KML/KMZ file to see your project area.",

  // Step 3: Activities
  activitiesTitle: "Select Activities",
  activitiesDesc: "Choose the activities that apply to your project",
  addOwnEntry: "Add your own entry",
  customActivity: "Custom activity",
  activityNamePlaceholder: "Activity name",
  activityDescPlaceholder: "Activity description",
  addActivity: "Add",
  cancel: "Cancel",
  activities: level1Activities,
  costCategories,
  costItems,
  costItemNotes,
  catalog: level1Catalog,
  validation: {
    summary: "Complete the carbon project summary: {fields}.",
    data: "Complete the carbon project data: {fields}.",
    costs: "Complete the carbon project costs: {fields}.",
    authenticationRequired: "Authentication required. Please connect your developer wallet again.",
    fields: {
      projectName: "project name",
      projectDescription: "project description",
      projectType: "project type",
      certifier: "certifier",
      methodology: "methodology",
      fieldDataOption: "field data option (field plot or remote sensing)",
      landOwnership: "land ownership",
      projectBoundaryMap: "project boundary map",
      projectArea: "project area",
      currency: "currency",
    },
  },

  // Step Chooser
  chooserTitle: "Choose project path",
  chooserSubtitle: "List issued credits or create a project for funding",
  chooserListCreditsTitle: "Already issued carbon credits",
  chooserListCreditsBtn: "List credits",
  chooserNewProjectTitle: "Create a project for funding",
  chooserNewProjectBtn: "Start project",

  // List Credits form
  lcProjectLink: "Project Link",
  lcPddUpload: "Upload your Project Design Document",
  lcLocation: "Location",
  lcLocationDesc: "Enter the project location or upload a KML to auto-detect",
  lcUploadKml: "Upload KML/KMZ",
  lcBiome: "Biome",
  lcState: "State",
  lcBoundary: "Project Boundary (KML/KMZ)",
  lcPhotos: "Photos",
  lcPhotosDesc: "Upload pictures of your project",
  lcPhotosUploadTitle: "Upload PNG or JPG",
  lcPhotosSubLabel: "PNG or JPG only",
  lcYearlyCreditSold: "Yearly credit sold",
  lcYearlyCreditSoldDesc: "Record the credits sold per year",
  lcPriceQuoteDesc: "Get a recommendation for the issued-credit listing price, then edit it before submission.",
  lcGetPriceRecommendation: "Get price recommendation",
  lcPriceQuoteError: "Price recommendation failed. You can still type a listing price manually.",
  listingPrice: "Listing Price",
  lcListingPricePlaceholder: "12.50",

  // Success screen
  formSubmitted: "Form submitted",
  successMessage:
    "Thank you for your submission, we'll get back to you as soon as possible!",
  seeHomepage: "See homepage",

  // Tab labels (resolved by FormStepTabs.vue)
  tabs: {
    summary: "Summary",
    data: "Data",
    activities: "Activities",
    timeline: "Timeline",
    credits: "Credits",
    costs: "Costs",
    price: "Price",
    review: "Review",
  },

  // Step: Timeline & Credit Schedule
  timeline: {
    scheduleBasicsTitle: "Project schedule",
    scheduleBasicsDesc:
      "Anchor the project's calendar. These four numbers drive validation, monitoring, credit generation, and cost timing.",
    projectStartYear: "Project start year",
    projectStartYearHint: "Validation, monitoring, and implementation begin.",
    creditingStartYear: "First credit year",
    creditingStartYearHint: "First year credits are generated.",
    modelingHorizonYears: "Years to model",
    modelingHorizonYearsHint:
      "Investment modeling horizon, capped at 20 years.",

    verificationScheduleTitle: "Verification & issuance schedule",
    verificationScheduleDesc:
      "Pick a preset that matches your methodology. Power users can fine-tune the derived values in the Advanced section below.",
    verificationPreset: "Verification & issuance preset",

    advancedAssumptions: "Advanced assumptions",
    advancedAssumptionsHint: "Override the values inferred from your presets.",
    verificationCycleYears: "Verification cycle (years)",
    firstVerificationYear: "First verification year",
    issuanceFrequency: "Issuance frequency",
    firstIssuanceYear: "First issuance year",
    advancedDerivedNote:
      "Leave any field blank to keep the value derived from your selected preset.",

    summaryTitle: "Schedule preview",
    summaryLine:
      "First verification in {firstVerification} • cycle every {cycle} year(s) • {issuance} • first issuance {firstIssuance}",

    presets: {
      standard_nature_based: "Standard: verify and issue every 5 years",
      faster_cycle: "Faster cycle: verify and issue every 3 years",
      annual_issuance: "Annual issuance",
      engineered_removal: "Engineered removal (annual)",
      gs_annual_reports_2y_first_verification:
        "Gold Standard: first verification by year 2, then up to every 5 years",
      car_forest_annual_reporting_full_every_6:
        "CAR forest: annual reporting, full verification every 6 reporting periods",
      puro_engineered_annual_after_audit:
        "Puro engineered: annual output audit, issuance after each audit",
      custom: "Custom (open advanced fields)",
    },
    presetDesc: {
      standard_nature_based:
        "Verification cycle: 5 years • Issuance: after each verification.",
      faster_cycle:
        "Verification cycle: 3 years • Issuance: after each verification.",
      annual_issuance: "Verification cycle: 1 year • Issuance: annual.",
      engineered_removal:
        "Verification cycle: 1 year • Issuance: annual. Best for DAC, BECCS, biochar.",
      gs_annual_reports_2y_first_verification:
        "Verification cycle: 2 years • Issuance: after each verification. Matches the Gold Standard cadence.",
      car_forest_annual_reporting_full_every_6:
        "Verification cycle: 6 years • Issuance: after each verification. Matches the CAR Mexico Forest Protocol cadence.",
      puro_engineered_annual_after_audit:
        "Verification cycle: 1 year • Issuance: after each audit. Matches Puro General Rules 4.3.",
      custom: "Edit the derived values in the Advanced section below.",
    },

    freq: {
      after_verification: "After verification",
      annual: "Annual",
    },

    errors: {
      invalidShape: "Timeline section is missing or malformed.",
      invalidPreset: "Verification & issuance preset is not recognized.",
      projectStartYearRange: "Project start year must be between {min} and {max}.",
      creditingStartYearRange: "First credit year must be between {min} and {max}.",
      modelingHorizonRange:
        "Years to model must be between {min} and {max}; contract pricing supports up to {max} years.",
      cycleRange: "Verification cycle must be between {min} and {max} years.",
      creditingBeforeStart: "First credit year cannot be before project start year.",
    },
  },
};
